import { useState, useRef } from 'react';
import { useAuthStore } from '../../store/authStore';
import { Button, Input, Card, Loading } from '../common';
import type { UpdateProfileData, UserPreferences } from '../../types/user';

interface ProfileEditFormProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export function ProfileEditForm({ onCancel, onSuccess }: ProfileEditFormProps) {
  const { user, updateProfile, uploadAvatar, deleteAvatar, isLoading, error } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<UpdateProfileData>({
    displayName: user?.displayName || '',
    email: user?.email || '',
    nickname: user?.nickname || '',
    birthDate: user?.birthDate || '',
    preferences: {
      theme: user?.preferences?.theme || 'dark',
      notifications: {
        email: user?.preferences?.notifications?.email ?? true,
        push: user?.preferences?.notifications?.push ?? false,
        readingReminders: user?.preferences?.notifications?.readingReminders ?? true,
      },
      privacy: {
        profileVisibility: user?.preferences?.privacy?.profileVisibility || 'private',
        shareReadings: user?.preferences?.privacy?.shareReadings ?? false,
      },
    },
  });
  
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!formData.displayName?.trim()) {
      errors.displayName = 'Display name is required';
    }
    
    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (formData.nickname && formData.nickname.trim()) {
      if (formData.nickname.length < 3) {
        errors.nickname = 'Nickname must be at least 3 characters';
      } else if (!/^[a-zA-Z0-9_]+$/.test(formData.nickname)) {
        errors.nickname = 'Nickname can only contain letters, numbers, and underscores';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof UpdateProfileData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePreferenceChange = (
    category: keyof UserPreferences,
    field: string,
    value: any
  ) => {
    setFormData(prev => {
      const currentPreferences = prev.preferences || {};
      const currentCategory = currentPreferences[category] || {};
      
      return {
        ...prev,
        preferences: {
          ...currentPreferences,
          [category]: {
            ...currentCategory,
            [field]: value,
          },
        },
      };
    });
  };

  const handleAvatarSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setValidationErrors(prev => ({ ...prev, avatar: 'Please select an image file' }));
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setValidationErrors(prev => ({ ...prev, avatar: 'Image must be smaller than 5MB' }));
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      // Clear any previous avatar errors
      setValidationErrors(prev => ({ ...prev, avatar: '' }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Upload avatar first if selected
      if (selectedFile) {
        await uploadAvatar(selectedFile);
      }
      
      // Update profile
      await updateProfile(formData);
      onSuccess();
    } catch (error) {
      // Error is handled by the store
      console.error('Profile update failed:', error);
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      await deleteAvatar();
      setAvatarPreview(null);
      setSelectedFile(null);
    } catch (error) {
      console.error('Avatar deletion failed:', error);
    }
  };

  const getCurrentAvatar = () => {
    if (avatarPreview) return avatarPreview;
    if (user?.avatarUrl) {
      const avatarUrl = user.avatarUrl;
      // Ensure avatar URL is absolute
      if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
        return avatarUrl;
      } else if (avatarUrl.startsWith('/')) {
        // Relative URL - prepend backend domain
        return `https://www.tarot-app.online${avatarUrl}`;
      }
      return avatarUrl;
    }
    return null;
  };

  return (
    <Card variant="elevated">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Edit Profile</h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>

        {error && (
          <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Avatar Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Profile Picture</h3>
          <p className="text-sm text-gray-400">Upload a JPG image up to 720x720. It will be resized to 128x128.</p>
          <div className="flex items-center gap-4">
            <div className="relative">
              {getCurrentAvatar() ? (
                <img
                  src={getCurrentAvatar()!}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white text-2xl font-bold">
                  {(user?.displayName || user?.nickname || user?.email)?.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
              >
                Change Picture
              </Button>
              {(user?.avatarUrl || avatarPreview) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleDeleteAvatar}
                  disabled={isLoading}
                  className="text-red-400 hover:text-red-300"
                >
                  Remove Picture
                </Button>
              )}
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg"
            onChange={handleAvatarSelect}
            className="hidden"
          />
          {validationErrors.avatar && (
            <p className="text-red-400 text-sm">{validationErrors.avatar}</p>
          )}
        </div>

        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Basic Information</h3>
          
          <Input
            label="Display Name"
            value={formData.displayName || ''}
            onChange={(e) => handleInputChange('displayName', e.target.value)}
            error={validationErrors.displayName}
            placeholder="Your display name"
            disabled={isLoading}
          />
          
          <Input
            label="Email"
            type="email"
            value={formData.email || ''}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={validationErrors.email}
            placeholder="your.email@example.com"
            disabled={isLoading}
          />
          
          <Input
            label="Nickname (Optional)"
            value={formData.nickname || ''}
            onChange={(e) => handleInputChange('nickname', e.target.value)}
            error={validationErrors.nickname}
            placeholder="nickname"
            disabled={isLoading}
          />
          
          <Input
            label="Birth Date (Optional)"
            type="date"
            value={formData.birthDate || ''}
            onChange={(e) => handleInputChange('birthDate', e.target.value)}
            error={validationErrors.birthDate}
            disabled={isLoading}
          />
        </div>

        {/* Preferences */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Preferences</h3>
          
          {/* Theme */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Theme
            </label>
            <select
              value={formData.preferences?.theme || 'dark'}
              onChange={(e) => handlePreferenceChange('theme', 'theme', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isLoading}
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="auto">Auto</option>
            </select>
          </div>

          {/* Notifications */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Notifications
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.preferences?.notifications?.email ?? true}
                  onChange={(e) => handlePreferenceChange('notifications', 'email', e.target.checked)}
                  className="w-4 h-4 text-primary-500 bg-gray-800 border-gray-600 rounded focus:ring-primary-500"
                  disabled={isLoading}
                />
                <span className="text-gray-300">Email notifications</span>
              </label>
              
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.preferences?.notifications?.push ?? false}
                  onChange={(e) => handlePreferenceChange('notifications', 'push', e.target.checked)}
                  className="w-4 h-4 text-primary-500 bg-gray-800 border-gray-600 rounded focus:ring-primary-500"
                  disabled={isLoading}
                />
                <span className="text-gray-300">Push notifications</span>
              </label>
              
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.preferences?.notifications?.readingReminders ?? true}
                  onChange={(e) => handlePreferenceChange('notifications', 'readingReminders', e.target.checked)}
                  className="w-4 h-4 text-primary-500 bg-gray-800 border-gray-600 rounded focus:ring-primary-500"
                  disabled={isLoading}
                />
                <span className="text-gray-300">Reading reminders</span>
              </label>
            </div>
          </div>

          {/* Privacy */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Profile Visibility
            </label>
            <select
              value={formData.preferences?.privacy?.profileVisibility || 'private'}
              onChange={(e) => handlePreferenceChange('privacy', 'profileVisibility', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={isLoading}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={formData.preferences?.privacy?.shareReadings ?? false}
              onChange={(e) => handlePreferenceChange('privacy', 'shareReadings', e.target.checked)}
              className="w-4 h-4 text-primary-500 bg-gray-800 border-gray-600 rounded focus:ring-primary-500"
              disabled={isLoading}
            />
            <span className="text-gray-300">Allow sharing of readings</span>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-6">
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? <Loading size="sm" /> : 'Save Changes'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}